// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const {
  ARTICLE_SUBTYPES,
  ARTICLE_SUBTYPES_KEYS
} = require('shared/model-types')
// Client V1 lib imports
const { getScrollParent } = require('client/lib/window')
// Client V2 common imports
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const {
  usePropsCallback,
  usePropsEventCallback
} = require('client-v2/common/hooks/callbacks')
const useClientEffect = require('client-v2/common/hooks/client-effect')
const useLineClampFitStyle = require('client-v2/common/hooks/line-clamp')
const useSymbol = require('client-v2/common/hooks/symbol')
const {
  useFullyVisibleViewportObserver
} = require('client-v2/common/hooks/viewport-observer')
const {
  articleDomainSearchHref
} = require('client-v2/common/models/article/actions')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const SeriesFeedArticleActions = require('./article-actions')

const bem = BemEntity('SeriesFeedArticle')

function SeriesFeedArticle(props) {
  const { article } = props
  // Prefer the subtype enum key in the stylesheet instead of the value
  const subtypeKey = ARTICLE_SUBTYPES_KEYS[article.subtype]
  const anchorId = useSymbol()
  const handleContentNavigate = usePropsEventCallback(props, 'onArticleContentNavigate')
  const handleDomainNavigate = usePropsEventCallback(props, 'onArticleDomainNavigate')
  const handleEmbedDisclose = usePropsCallback(props, 'onArticleEmbedDisclose')
  // onArticleSeen visibility observer
  const innerRef = React.useRef()
  useFullyVisibleViewportObserver({
    elementRef: innerRef,
    onVisible: usePropsCallback(props, 'onArticleSeen')
  })
  // Focus observer
  const focusRef = React.useRef()
  const isFocused = useFocusObserver(focusRef)
  // Render
  return (
    tag('article', {
      ref: focusRef,
      className: classNames(
        props.className,
        bem.block({
          subtype: subtypeKey
        })
      ),
      dataset: {
        urlId: article['url_id']
      }
    },
      tag('div', {
        ref: innerRef,
        className: bem.element('inner')
      },
        tag('div', {
          className: bem.element('details')
        },
          tag('a', {
            className: bem.element('domain'),
            href: articleDomainSearchHref(article),
            onClick: handleDomainNavigate
          },
            article.hostname
          ),
          tag(SeriesFeedArticleContent, {
            article,
            onContentNavigate: handleContentNavigate
          }),
          tag(SeriesFeedArticleCallToAction, {
            article,
            onContentNavigate: handleContentNavigate
          })
        ),
        tag(SeriesFeedArticleImage, {
          article,
          onContentNavigate: handleContentNavigate,
          onEmbedDisclose: handleEmbedDisclose
        }),
        tag(SeriesFeedArticleActions, {
          ...props,
          anchorId,
          className: bem.element('actions', {
            hidden: !isFocused
          })
        })
      )
    )
  )
}

function SeriesFeedArticleContent({
  article,
  onContentNavigate
}) {
  const titleRef = React.useRef()
  const titleStyle = useLineClampFitStyle({
    domRef: titleRef
  })
  const descriptionRef = React.useRef()
  const descriptionStyle = useLineClampFitStyle({
    domRef: descriptionRef
  })
  return (
    tag('a', {
      className: bem.element('content'),
      href: article.url,
      rel: 'external nofollow noopener',
      target: '_blank',
      onClick: onContentNavigate
    },
      tag('p', {
        ref: titleRef,
        className: bem.element('title'),
        style: titleStyle
      },
        article.title
      ),
      !!article.description && (
        tag('p', {
          ref: descriptionRef,
          className: bem.element('description'),
          style: descriptionStyle
        },
          article.description
        )
      )
    )
  )
}

function SeriesFeedArticleCallToAction({
  article,
  onContentNavigate
}) {
  return (
    tag('a', {
      'aria-hidden': true,
      className: bem.element('call-to-action'),
      href: article.url,
      rel: 'external nofollow noopener',
      target: '_blank',
      onClick: onContentNavigate
    },
      article.subtype === ARTICLE_SUBTYPES.VIDEO
      ? `Watch on ${article.hostname}`
      : 'Read more'
    )
  )
}

function SeriesFeedArticleImage({
  article,
  onContentNavigate,
  onEmbedDisclose
}) {
  const { embedUrl, image, md5image } = article
  const imageSources = React.useMemo(
    () => [ image, md5image ],
    [ image, md5image ]
  )
  const imageElement = (
    tag(LazyImage, {
      className: bem.element('image'),
      href: article.url,
      rel: 'external nofollow noopener',
      target: '_blank',
      sources: imageSources,
      onClick: onContentNavigate
    })
  )
  if (!embedUrl) {
    return imageElement
  }
  return (
    tag('div', {
      className: bem.element('embed-container')
    },
      imageElement,
      tag(EmbeddedPlayer, {
        className: bem.element('embed-player'),
        src: embedUrl,
        onDisclose: onEmbedDisclose
      })
    )
  )
}

/**
 * React hook to track whether the element is currently scrolled within the viewport
 * @param {React.RefObject<Element>} focusRef
 * @returns {boolean}
 */
function useFocusObserver(focusRef) {
  const [ isFocused, resetFocused ] = useFullyVisibleViewportObserver({
    elementRef: focusRef
  })
  useClientEffect(
    () => {
      if (!isFocused) {
        return
      }
      const focusEl = focusRef.current
      const scrollableEl = getScrollParent(focusEl)
      const onScroll = () => {
        const vpTop = scrollableEl.scrollTop
        const vpBottom = vpTop + scrollableEl.clientHeight
        const elTop = focusEl.offsetTop
        const elBottom = elTop + focusEl.offsetHeight
        if (elTop < vpTop || elBottom > vpBottom) {
          resetFocused()
        }
      }
      const onScrollDebounced = _.throttle(onScroll, 100)
      scrollableEl.addEventListener('scroll', onScrollDebounced)
      return () => {
        scrollableEl.removeEventListener('scroll', onScrollDebounced)
        onScrollDebounced.cancel()
      }
    },
    [ focusRef, isFocused, resetFocused ]
  )
  return isFocused
}

module.exports = SeriesFeedArticle
